import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faArrowUp, faCheckCircle, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import {
  faTwitter,
  faDiscord,
  faTelegram,
  faLinkedin,
} from '@fortawesome/free-brands-svg-icons';

const Footer: React.FC = () => {
  const [email, setEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  // Formspree endpoint (same as contact form)
  const FORMSPREE_URL = 'https://formspree.io/f/xeorjnpg';

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewsletterStatus('sending');

    try {
      const response = await fetch(FORMSPREE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          _subject: 'New Newsletter Subscription',
          type: 'newsletter',
        }),
      });

      if (response.ok) {
        setNewsletterStatus('success');
        setStatusMessage('Thank you for subscribing! Check your email for confirmation.');
        setEmail('');
        setTimeout(() => {
          setNewsletterStatus('idle');
          setStatusMessage('');
        }, 5000);
      } else {
        throw new Error('Failed to subscribe');
      }
    } catch (error) {
      setNewsletterStatus('error');
      setStatusMessage('Oops! Something went wrong. Please try again.');
      setTimeout(() => {
        setNewsletterStatus('idle');
        setStatusMessage('');
      }, 5000);
    }
  };

  const footerLinks = {
    Navigate: [
      { label: 'Home', href: '#' },
      { label: 'Services', href: '#services' },
      { label: 'Solutions', href: '#solutions' },
      { label: 'About', href: '#about' },
      { label: 'Contact', href: '#contact' },
    ],
  };

  const socialLinks = [
    { icon: faTwitter, href: '#', label: 'Twitter' },
    { icon: faDiscord, href: '#', label: 'Discord' },
    { icon: faTelegram, href: '#', label: 'Telegram' },
    { icon: faLinkedin, href: '#', label: 'LinkedIn' },
  ];

  return (
    <footer className="relative border-t border-linen/10">
      {/* Newsletter Section */}
      <div className="border-b border-linen/10">
        <div className="max-container section-padding py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-xl p-8 text-center"
          >
            <h3 className="text-2xl font-semibold text-linen mb-2">
              Stay Updated with <span className="gradient-text">Web3 Trends</span>
            </h3>
            <p className="text-linen-400 mb-6">
              Get weekly insights on blockchain marketing and industry news
            </p>

            <form onSubmit={handleNewsletterSubmit} className="flex flex-col items-center">
              <div className="flex flex-col sm:flex-row gap-4 max-w-md w-full">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 px-4 py-3 bg-raisin-800 border border-linen/20 rounded-lg focus:border-verdigris focus:outline-none transition-colors text-linen placeholder-linen-500"
                  required
                  disabled={newsletterStatus === 'sending'}
                />
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary"
                  disabled={newsletterStatus === 'sending'}
                >
                  {newsletterStatus === 'sending' ? 'Subscribing...' : 'Subscribe'}
                </motion.button>
              </div>

              {/* Status Messages */}
              {newsletterStatus !== 'idle' && statusMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mt-4 p-3 rounded-lg flex items-center space-x-2 ${
                    newsletterStatus === 'success'
                      ? 'bg-verdigris/20 text-verdigris'
                      : 'bg-persian/20 text-persian'
                  }`}
                >
                  <FontAwesomeIcon
                    icon={newsletterStatus === 'success' ? faCheckCircle : faExclamationCircle}
                    className="text-lg"
                  />
                  <span className="text-sm">{statusMessage}</span>
                </motion.div>
              )}
            </form>
          </motion.div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-container section-padding py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Brand Column */}
          <div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center space-x-2 mb-4"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-persian to-verdigris rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">C</span>
              </div>
              <span className="text-2xl font-display font-bold text-linen">
                ChainReach
              </span>
            </motion.div>
            <p className="text-linen-400 text-sm mb-4">
              Leading Web3 marketing agency helping blockchain projects reach their full potential.
            </p>
            <a
              href="mailto:contact@chainreach.io"
              className="text-verdigris hover:text-verdigris-400 transition-colors text-sm"
            >
              contact@chainreach.io
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-linen font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {footerLinks.Navigate.map((link) => (
                <li key={link.label}>
                  <motion.a
                    href={link.href}
                    whileHover={{ x: 3 }}
                    className="text-linen-400 hover:text-verdigris text-sm transition-colors"
                  >
                    {link.label}
                  </motion.a>
                </li>
              ))}
            </ul>
          </div>

          {/* Social Links */}
          <div>
            <h4 className="text-linen font-semibold mb-4">Connect With Us</h4>
            <div className="flex space-x-3">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  whileHover={{ scale: 1.2, y: -2 }}
                  className="w-8 h-8 glass rounded-lg flex items-center justify-center text-linen-400 hover:text-verdigris transition-colors"
                  aria-label={social.label}
                >
                  <FontAwesomeIcon icon={social.icon} />
                </motion.a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-linen/10">
        <div className="max-container section-padding py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center">
            <p className="text-linen-400 text-sm mb-4 sm:mb-0">
              © 2025 ChainReach. All rights reserved. Built with{' '}
              <FontAwesomeIcon icon={faHeart} className="text-persian" /> for Web3
            </p>

            {/* Back to Top Button */}
            <motion.button
              onClick={scrollToTop}
              whileHover={{ scale: 1.1, y: -2 }}
              whileTap={{ scale: 0.9 }}
              className="w-10 h-10 glass rounded-lg flex items-center justify-center text-linen-400 hover:text-verdigris transition-colors"
              aria-label="Scroll to top"
            >
              <FontAwesomeIcon icon={faArrowUp} />
            </motion.button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;